import { Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap, shareReplay } from 'rxjs/operators';
import { API } from '@techextensor/tab-core-utility';
import { Constants } from '../const/constants';
import { environment } from '../../../environments/environment';

@Injectable()
export class CacheInterceptor implements HttpInterceptor {
  private cache = new Map<string, Observable<HttpEvent<unknown>>>();
  private readonly maxCacheSize = 50;

  /**
   * Intercepts an HTTP request and checks if the response is cached.
   * If the request is cached, it returns the cached response.
   * If the request is not cached, it adds the response to the cache and returns it.
   *
   * The cache key is determined based on the URL and the request body.
   * The cache size is limited to 100 entries.
   *
   * @param request The HTTP request to intercept.
   * @param next The next HTTP handler to call.
   * @returns The response to the request, which is either the cached response or a new response.
   */
  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    try {
      const cacheKey = this.getCacheKey(request);
      if (!cacheKey) {
        return next.handle(request);
      }

      // Check cache first
      const cachedResponse = this.cache.get(cacheKey);
      if (cachedResponse) {
        return cachedResponse;
      }

      // Create new cached request
      const shared = next.handle(request).pipe(
        tap((event: HttpEvent<unknown>) => {
          if (event instanceof HttpResponse) {
            const responseBody = event?.body as any;
            if (!responseBody?.isSuccess) {
              this.cache.delete(cacheKey);
            }
          }
        }),
        shareReplay(1)
      );

      /**
       * Adds the new cached response to the cache.
       * If the cache is at its maximum size, it will remove the oldest entry
       * to make room for the new one.
       */
      this.addToCache(cacheKey, shared);
      return shared;

    } catch (error) {
      console.error(error);
      return next.handle(request);
    }
  }

  /**
   * Generates a unique cache key for a given HTTP request.
   * The cache key is determined based on the URL and the request body.
   *
   * @param request The HTTP request for which to generate the cache key.
   * @returns A unique string to be used as the cache key, or null if the request should not be cached.
   */
  private getCacheKey(request: HttpRequest<unknown>): string | null {
    // Cache screen requests by URL
    if (request.url?.includes(environment.apiServiceUrls.metadataService + API.screen)) {
      return `screen:${request.urlWithParams}`;
    }

    // Cache template requests by URL and hashed payload
    if (request.url?.includes(Constants.Detail_TABD_Templates)) {
      return `template:${request.url}:${this.hashPayload(request.body)}`;
    }

    // Return null if the request should not be cached
    return null;
  }

  /**
   * Calculates a hash from the given payload.
   * The hash is a MD5 string calculated from the concatenated and sorted values of the payload.
   * If the payload is null or undefined, the string 'empty' is returned.
   *
   * @param payload the payload to calculate the hash from
   * @returns the hash as a string
   */
  private hashPayload(payload: any): string {
    if (!payload) return 'empty';

    const values: string[] = [];
    this.extractObjectValues(payload, values);
    values.sort();

    return this.createHash(values.join('|'));
  }

  /**
   * Extracts the values from the given object and adds them to the given array.
   * The object can be a nested object or an array.
   *
   * @param obj the object to extract the values from
   * @param values the array to add the values to
   */
  private extractObjectValues(obj: any, values: string[]): void {
    if (obj === null || obj === undefined) {
      // If the object is null or undefined, add 'null' to the values array
      values.push('null');
      return;
    }

    if (typeof obj !== 'object') {
      // If the object is not an object, add its string value to the values array
      values.push(String(obj));
      return;
    }

    if (Array.isArray(obj)) {
      // If the object is an array, iterate over its elements and recursively call this method
      obj.forEach(item => this.extractObjectValues(item, values));
      return;
    }

    // Iterate over the object's properties and recursively call this method
    Object.keys(obj).forEach(key => {
      if (key !== 'hashValue' && obj.hasOwnProperty(key)) {
        // Add the key to the values array
        values.push(key);
        // Recursively call this method for each property
        this.extractObjectValues(obj[key], values);
      }
    });
  }

  /**
   * Creates a hash value for the given string.
   *
   * This function is a simple implementation of the djb2 hash algorithm.
   * It takes a string as input and returns a hash value as a string.
   *
   * @param str the string to hash
   * @returns a hash value as a string
   */
  private createHash(str: string): string {
    let hash = 0;
    // Iterate over each character in the string
    for (let i = 0; i < str.length; i++) {
      // Calculate the hash value
      hash = ((hash << 5) - hash) + str.charCodeAt(i);
      // Make sure the hash value is positive
      hash = hash & hash;
    }
    // Return the hash value as a string
    return Math.abs(hash).toString(36);
  }

  /**
   * Adds the given data to the cache under the provided key.
   *
   * If the cache is at its maximum size, it will remove the oldest entry
   * to make room for the new one.
   *
   * @param key the key to store the data under
   * @param data the data to store in the cache
   */
  private addToCache(key: string, data: Observable<HttpEvent<unknown>>): void {
    // If the cache is full
    if (this.cache.size >= this.maxCacheSize) {
      this.cache.clear();
    }

    // Add the new entry to the cache
    this.cache.set(key, data);
  }
}